import { useMemo, useCallback } from 'react';
import { CustomMessageBar } from '@shared/components';
import { Button } from '@fluentui/react-components';
import { useNavigate } from 'react-router-dom';
import { TFolder, TPath, TResolvedItemsMap } from '@modules/items';
import { FULL_PATH_SETUP_ITEM } from '@modules/items';
import { FULL_PATH_INLINE_BLOCK_DETECTION_ITEM } from '../../constants';
import { groupByItem, mapToObject } from '../../helpers/group.helper';
import DetectionScaffold from '../shared/layout/DetectionScaffold';
import SummaryTopBar from '../shared/layout/SummaryTopBar';
import SummarySectionsList from '../shared/layout/SummarySectionsList';
import { IInlineBlockDetectionRun } from '../../types/inlineBlocks.types';

type TProps = {
  run: IInlineBlockDetectionRun;
  resolvedItems: TResolvedItemsMap;
  folder: TFolder;
};

/**
 * Displays inserted inline-blocks, grouped by source document.
 */
const InlineBlockDetectionSummaryContent = ({ run, resolvedItems, folder }: TProps) => {
  const navigate = useNavigate();
  const handleStartNewDetection = () => {
    navigate(`/product/${folder._id}/library-detection`);
  };

  // stable filter declared **once**
  const onlyInserted = useCallback((r: { inserted: boolean }) => !!r.inserted, []);
  const inserted = useMemo(
    () => run.detectedInlineBlocks.filter(onlyInserted),
    [run.detectedInlineBlocks, onlyInserted]
  );

  const grouped = useMemo(() => mapToObject(groupByItem(inserted)), [inserted]);
  // `Object.entries` is O(n) operation, so we memoize it
  const groupedEntries = useMemo(() => Object.entries(grouped), [grouped]);

  const totalOccurrences = inserted.length;
  const totalDocs = useMemo(() => new Set(inserted.map((x) => x.sourceItem)).size, [inserted]);

  // "fullPath" for the FoldersNavDrawer
  const fullPath: TPath = [
    { _id: folder._id, name: folder.name, accessRight: folder.accessRight },
    FULL_PATH_SETUP_ITEM, // Setup
    FULL_PATH_INLINE_BLOCK_DETECTION_ITEM, // Inline-block detection
  ];

  return (
    <DetectionScaffold folder={folder} fullPath={fullPath} selectedValue="library-detection" maxWidth="max-w-screen-xl">
      <SummaryTopBar
        success={{
          count: totalOccurrences,
          docs: totalDocs,
          nounSingular: 'library item',
        }}
      />

      {groupedEntries.length === 0 ? (
        <CustomMessageBar
          intent="info"
          title="No library items inserted."
          body="Please make sure you have selected the correct folders and run the detection again."
        />
      ) : (
        <SummarySectionsList groupedEntries={groupedEntries} resolvedItems={resolvedItems} variant="inline-blocks" />
      )}

      <div className="flex w-full justify-end py-3">
        <Button appearance="secondary" size="medium" onClick={handleStartNewDetection} className="w-fit">
          Start new detection
        </Button>
      </div>
    </DetectionScaffold>
  );
};

export default InlineBlockDetectionSummaryContent;
